import { useEffect, useState, useRef } from 'react';
import styles from './CircleDownCounter.module.css';

export default function CircleDownCounter({ initialSeconds, onExpired }) {
  const [seconds, setSeconds] = useState(Math.max(0, Math.floor(initialSeconds)));
  const timerRef = useRef(null);

  useEffect(() => {
    // Reset seconds when initialSeconds changes
    setSeconds(Math.max(0, Math.floor(initialSeconds)));

    // Clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Start new timer
    timerRef.current = setInterval(() => {
      setSeconds((prevSeconds) => {
        const newSeconds = prevSeconds - 1;
        if (newSeconds <= 0) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          onExpired();
          return 0;
        }
        return newSeconds;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [initialSeconds, onExpired]);

  // Calculate progress percentage
  const progress = initialSeconds > 0 ? (seconds / initialSeconds) * 100 : 0;

  // Calculate minutes and seconds
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  const timeDisplay = `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;

  // Calculation for SVG circle
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // calculate the angle for the point (at the end of the progress arc)
  const dotAngle = -90 + (progress / 100) * 360;
  // Calculate the position of the point on the circle
  const centerX = 50;
  const centerY = 50;
  const dotX = centerX + radius * Math.cos((dotAngle * Math.PI) / 180);
  const dotY = centerY + radius * Math.sin((dotAngle * Math.PI) / 180);

  const svgContent = (
    <>
      <svg className={styles.timerSvg} viewBox="0 0 100 100">
        <circle
          className={styles.baseCircle}
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          strokeWidth="4"
        />
        <circle className={styles.darkCircle} cx="50" cy="50" r={radius} fill="none" />
        <circle
          className={styles.progressCircle}
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          strokeWidth="4"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform="rotate(-90 50 50)"
        />
        <circle className={styles.progressDot} cx={dotX} cy={dotY} r="3" />
      </svg>
      <div className={styles.timeDisplay}>{timeDisplay}</div>
    </>
  );

  return <div className={styles.circularTimer}>{svgContent}</div>;
}
